<script>
  import t from '#lib/i18n.svelte.js';
  import SearchSelect from '#lib/SearchSelect.svelte';
  import Dialog from '#lib/Dialog.svelte';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import TextInput from '#lib/TextInput.svelte';
  import { fetch_json } from '#lib/util.js';
  import { getContext } from 'svelte';
 
  let site_settings = getContext("site_settings");

  let { 
    /** @type {number} conversation_id */
    conversation_id, 
    /** @type {string} current_tag_key */
    current_tag_key, 
    /** @type {() => void} callback to be called when the tag is changed */
    on_tag_change = () => {} 
  } = $props();
  
  let all_tags = $state([]);
  let search_term = $state('');
  let show_dialog = $state(false);
  let editing_tag = $state(null);
  let editing_tag_name = $state('');
  
  // Load all tags on mount
  $effect(() => {
    load_tags();
  });
  
  async function load_tags() {
    try {
      all_tags = await fetch_json('GET', '/api/get-all-tags');
    } catch (error) {
      console.error('Failed to load tags:', error);
    }
  }
  
  async function assign_tag(tag_key) {
    try {
      await fetch_json('POST', '/api/assign-conversation-tag', { conversation_id, tag_key });
      
      on_tag_change(tag_key);
    } catch (error) {
      console.error('Failed to assign tag:', error);
    }
  }
  
  async function create_and_assign_tag(tag_key) {
    try {
      await fetch_json('POST', '/api/assign-conversation-tag', { conversation_id, tag_key });
      
      on_tag_change(tag_key);
      await load_tags(); // Reload tags to include the new one
    } catch (error) {
      console.error('Failed to create tag:', error);
    }
  }
  
  async function update_tag(new_key) {
    try {
      await fetch_json('POST', '/api/update-conversation-tag', { tag_id: editing_tag.tag_id, new_tag_key: new_key });
      
      await load_tags();
      if (current_tag_key === editing_tag.tag_key) {
        on_tag_change(new_key);
      }
    } catch (error) {
      console.error('Failed to update tag:', error);
    }
  }
  
  async function delete_tag() {
    try {
      await fetch_json('POST', '/api/delete-conversation-tag', { tag_id: editing_tag.tag_id });
      
      await load_tags();
      if (current_tag_key === editing_tag.tag_key) {
        on_tag_change(null);
      }
    } catch (error) {
      console.error('Failed to delete tag:', error);
    }
  }
  
  // Convert tags to SearchSelect options with dynamic new tag option
  let tag_options = $derived.by(() => {
    const options = all_tags.map(tag => ({
      value: tag.tag_key,
      type: 'string',
      title: tag.tag_key,
      tag_data: tag
    }));
    
    // Add new tag option if search term is not empty and doesn't match existing tags
    if (search_term && search_term.trim().length > 0) {
      const is_new_tag = !all_tags.some(tag => 
        tag.tag_key.toLowerCase() === search_term.trim().toLowerCase()
      );
      
      if (is_new_tag) {
        options.push({
          value: search_term.trim(),
          type: 'string',
          title: search_term.trim(),
          icon: 'plus',
          description: t('create_new_tag'),
          is_new_tag: true
        });
      }
    }
    
    return options;
  });
  
  function handle_select(option, event) {
    if (!option) return;
    
    if (option.value === '' || option.value === null) {
      assign_tag(null);
    } else if (option.is_new_tag) {
      create_and_assign_tag(option.value);
    } else {
      assign_tag(option.value);
    }
  }
  
  function handle_search(term, callback) {
    search_term = term;
    if (callback) {
      setTimeout(() => callback(true), 100);
    }
  }
  
  function open_tag_dialog(tag_data, event) {
    event.stopPropagation();
    event.preventDefault();
    editing_tag = tag_data;
    editing_tag_name = tag_data.tag_key;
    show_dialog = true;
  }
  
  async function save_edit() {
    if (editing_tag && editing_tag_name.trim()) {
      await update_tag(editing_tag_name.trim());
      close_dialog();
    }
  }
  
  async function confirm_delete() {
    if (editing_tag) {
      window.confirm(t('confirm_delete_tag'));
      await delete_tag();
      close_dialog();
    }
  }
  
  function close_dialog() {
    show_dialog = false;
    editing_tag = null;
    editing_tag_name = '';
  }
</script>

<SearchSelect
  options={tag_options}
  value={current_tag_key || ''}
  on_select={handle_select}
  on_search={handle_search}
  placeholder={t('add_tag')}
  width="small"
  allow_null={true}
  enable_search={true}
  min_search_term_length={1}
  container="inline"
>
  {#snippet option_tools_snippet(option, slot_props)}
    {#if !option.is_remove_option && !option.is_new_tag && option.tag_data}
      <button 
        class="tag-action-button delete-button p-1 rounded hover:bg-emphasis" 
        onclick={(e) => open_tag_dialog(option.tag_data, e)}
        title={t('tag_actions')}
      >
        <ButtonLabel start_icon="dot-dot-dot" />
      </button>
    {/if}
  {/snippet}
</SearchSelect>

<!-- Dialog for Edit and Delete -->
{#if show_dialog}
  <Dialog on_close={close_dialog} class="tag-dialog">
    <main class="flex-column py">
      <header class="flex-wrap items-center gap-x g-6 justify-between px py p-6">
        <label for="tag-name-input" class="text-body leading-trim">{t('edit_tag')}</label>
        <button onclick={close_dialog} title={t('close')}>
          <ButtonLabel variant="brand ghostly" start_icon="close" />
        </button>
      </header>
      
      <section class="px p-6">
        <TextInput
          id="tag-name-input"
          type="text" 
          bind:value={editing_tag_name}
          placeholder={t('tag_name')}
          onkeydown={(e) => e.key === 'Enter' && save_edit()}
        />
      </section>

      <footer class="flex-row items-center justify-between px py p-6 gap-x g-4">
        <button onclick={confirm_delete} class="delete-button">
          <ButtonLabel start_icon="trash" variant="system-destructive outlined">
            {t('delete_tag')}
          </ButtonLabel>
        </button>
        <button onclick={save_edit} disabled={!editing_tag_name.trim()}>
          <ButtonLabel variant="brand {site_settings.primary_control_surface}" align={site_settings.control_surface_align}>
            {t('save')}
          </ButtonLabel>
        </button>
      </footer>
    </main>
  </Dialog>
{/if}
